/**
 * IndexedDB persistence (no localStorage for game state — hard requirement).
 * Two stores: 'kv' (settings, unlocks, run snapshot) and 'scores'.
 */
const DB_NAME = 'ruin'
const DB_VERSION = 1

export interface Settings {
  classicAim: boolean
  sfxVolume: number
  musicVolume: number
  screenShake: boolean
  haptics: boolean
  reducedMotion: 'auto' | 'on' | 'off'
  inputOverride: 'auto' | 'desktop' | 'touch'
  /** live DAISE commentary (issue #38) — default-on per the decision on issue #36 */
  liveCommentary: boolean
}

export const DEFAULT_SETTINGS: Settings = {
  classicAim: false,
  sfxVolume: 0.8,
  musicVolume: 0.5,
  screenShake: true,
  haptics: true,
  reducedMotion: 'auto',
  inputOverride: 'auto',
  liveCommentary: true
}

export interface ScoreEntry {
  score: number
  floor: number
  mode: 'campaign' | 'endless'
  won: boolean
  date: number
  /** world seed — replayable via RE-RUN; absent on entries saved before seeds were recorded */
  seed?: number
  /** daily-broadcast runs only: the UTC date string the seed derives from (YYYY-MM-DD) */
  daily?: string
}

/** lifetime participant dossier — kv-persisted, same pattern as settings */
export interface LifetimeStats {
  runs: number
  kills: number
  foodEaten: number
  /** seconds of simulated play across all runs */
  timeOnAir: number
  deepestFloor: number
  /** run-ending expirations (not spent lives) */
  deaths: number
  campaignWins: number
}

export const DEFAULT_LIFETIME: LifetimeStats = {
  runs: 0,
  kills: 0,
  foodEaten: 0,
  timeOnAir: 0,
  deepestFloor: 0,
  deaths: 0,
  campaignWins: 0
}

let dbPromise: Promise<IDBDatabase> | null = null

function open(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION)
    req.onupgradeneeded = () => {
      const db = req.result
      if (!db.objectStoreNames.contains('kv')) db.createObjectStore('kv')
      if (!db.objectStoreNames.contains('scores')) db.createObjectStore('scores', { autoIncrement: true })
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
  return dbPromise
}

function tx<T>(store: string, mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return open().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const t = db.transaction(store, mode)
        const req = fn(t.objectStore(store))
        req.onsuccess = () => resolve(req.result)
        req.onerror = () => reject(req.error)
      })
  )
}

export async function kvGet<T>(key: string): Promise<T | undefined> {
  try {
    return (await tx<unknown>('kv', 'readonly', (s) => s.get(key))) as T | undefined
  } catch {
    return undefined
  }
}

export async function kvSet(key: string, value: unknown): Promise<void> {
  try {
    await tx('kv', 'readwrite', (s) => s.put(value, key))
  } catch {
    // persistence is best-effort; the game must keep running without it
  }
}

export async function addScore(entry: ScoreEntry): Promise<void> {
  try {
    await tx('scores', 'readwrite', (s) => s.add(entry))
  } catch {
    /* best-effort */
  }
}

/**
 * All stored entries, best-first. Legacy rows (saved before mode/seed were
 * recorded) normalize in place: mode defaults to campaign, seed stays absent.
 */
export async function allScores(): Promise<ScoreEntry[]> {
  try {
    const all = (await tx<unknown[]>('scores', 'readonly', (s) => s.getAll())) as Partial<ScoreEntry>[]
    return all
      .map(
        (e): ScoreEntry => ({
          ...e,
          score: e.score ?? 0,
          floor: e.floor ?? 1,
          mode: e.mode ?? 'campaign',
          won: e.won ?? false,
          date: e.date ?? 0
        })
      )
      .sort((a, b) => b.score - a.score)
  } catch {
    return []
  }
}

export async function topScores(limit = 10): Promise<ScoreEntry[]> {
  return (await allScores()).slice(0, limit)
}

export async function loadLifetime(): Promise<LifetimeStats> {
  const s = await kvGet<Partial<LifetimeStats>>('lifetime')
  return { ...DEFAULT_LIFETIME, ...(s ?? {}) }
}

export async function saveLifetime(s: LifetimeStats): Promise<void> {
  await kvSet('lifetime', s)
}

/** archive fragments: owned record numbers (1-based) — collection survives runs */
export async function loadArchiveFragments(): Promise<number[]> {
  return (await kvGet<number[]>('archiveFragments')) ?? []
}

export async function saveArchiveFragments(ids: number[]): Promise<void> {
  await kvSet('archiveFragments', ids)
}

export async function loadSettings(): Promise<Settings> {
  const s = await kvGet<Partial<Settings>>('settings')
  return { ...DEFAULT_SETTINGS, ...(s ?? {}) }
}

export async function saveSettings(s: Settings): Promise<void> {
  await kvSet('settings', s)
}
